'use client';
import { css, styled } from '@mui/material/styles';
import isPropValid from '@emotion/is-prop-valid';
import {
  gutterFlexible,
  gutterSmAndUp,
  gutterXs,
  primaryFontFamily,
  warm,
} from '@style/variables.js';
import { AlignH, AlignV, FlexDirection } from '@repo/ui/src/types';
import { HorizontalAlignmentEnum, VerticalAlignmentEnum } from '@repo/ui/src/enums/shared-enums';

const bulletSize = '32px';

interface CardProps {
  imgUrl: string | null;
}

export const ToutContentCardContainer = styled('div', {
  shouldForwardProp: isPropValid,
})<CardProps>`
  min-height: inherit;
  width: 100%;
  background-image: ${(props) => (props.imgUrl ? `url("${props.imgUrl}")` : null)};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  position: relative;

  .video {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }
`;

interface CardInnerProps extends CardProps {
  verticalAlignment: AlignV;
  horizontalAlignment: AlignH;
  centerGradient: boolean;
  gradientDirection: string;
  textAlign: AlignH;
  fullWidthAndHeight?: boolean;
}

export const InnerToutContentContainer = styled('div', {
  shouldForwardProp: isPropValid,
})<CardInnerProps>`
  display: flex;
  align-items: ${(props) =>
    props.verticalAlignment === VerticalAlignmentEnum.Top
      ? 'flex-start'
      : props.verticalAlignment === VerticalAlignmentEnum.Bottom
        ? 'flex-end'
        : 'center'};
  justify-content: ${(props) =>
    props.horizontalAlignment === HorizontalAlignmentEnum.Left
      ? 'flex-start'
      : props.horizontalAlignment === HorizontalAlignmentEnum.Right
        ? 'flex-end'
        : 'center'};

  padding: ${gutterXs};
  min-height: inherit;
  position: relative;
  background: ${(props) =>
    props.imgUrl
      ? 'linear-gradient(to bottom right, rgba(0, 0, 0, .7) 0%, rgba(0, 0, 0, 0) 100%)'
      : null};

  ${(props) =>
    props.fullWidthAndHeight
      ? css`
          height: 100%;
          width: 100%;
        `
      : ''}

  ${(props) => props.theme.breakpoints.up('sm')} {
    padding-top: ${Number(gutterFlexible) * 2}vw;
    padding-bottom: ${Number(gutterFlexible) * 2}vw;
    padding-left: ${(props) =>
      props.horizontalAlignment === HorizontalAlignmentEnum.Right
        ? '35%'
        : `${Number(gutterFlexible) * 2}vw`};
    padding-right: ${(props) =>
      props.horizontalAlignment === HorizontalAlignmentEnum.Left
        ? '35%'
        : `${Number(gutterFlexible) * 2}vw`};
    background: ${(props) =>
      props.imgUrl
        ? props.centerGradient
          ? 'radial-gradient(circle, rgba(0, 0, 0, .5) 18%, rgba(0, 0, 0, 0) 100%)'
          : `linear-gradient(to ${props.gradientDirection}, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 90%)`
        : null};

    & .description {
      max-width: 440px;
      margin-left: ${(props) => (props.textAlign === HorizontalAlignmentEnum.Left ? 0 : 'auto')};
      margin-right: ${(props) => (props.textAlign === HorizontalAlignmentEnum.Right ? 0 : 'auto')};
    }
  }

  ${(props) => props.theme.breakpoints.up('md')} {
    padding-left: ${(props) =>
      props.horizontalAlignment === HorizontalAlignmentEnum.Right
        ? '55%'
        : `${Number(gutterFlexible) * 2}vw`};
    padding-right: ${(props) =>
      props.horizontalAlignment === HorizontalAlignmentEnum.Left
        ? '55%'
        : `${Number(gutterFlexible) * 2}vw`};
  }
`;

export const TitledSplitToutContentContainer = styled('div')`
  .divider {
    &::after {
      content: '';
      height: 0.5rem;
      width: 5rem;
      display: block;
      margin-left: auto;
      margin-right: auto;
      background-color: ${({ theme }) => theme.palette.primary.main};
    }
  }

  .dividerTop {
    &::after {
      margin-bottom: 1.5rem;
    }
  }

  .dividerBottom {
    &::after {
      margin-top: 1.5rem;
    }
  }

  .heading {
    padding-top: ${gutterXs};
    padding-bottom: ${gutterSmAndUp};
    ${(props) => props.theme.breakpoints.down('md')} {
      padding-bottom: ${gutterXs};
    }
  }

  .leftColumn {
    padding-right: 0.75rem;
    padding-bottom: ${gutterXs};
    ${(props) => props.theme.breakpoints.down('md')} {
      padding-right: 0;
    }
  }

  .rightColumn {
    padding-left: 0.75rem;
    padding-bottom: ${gutterXs};
    ${(props) => props.theme.breakpoints.down('md')} {
      padding-left: 0;
    }
  }
`;

export const ToutContentWithMarkdownContainer = styled('div', {
  shouldForwardProp: isPropValid,
})<MarkdownProps>`
  .split {
    display: flex;
    flex-direction: ${(props) => props.flexDisplay || (props.smAndDown ? 'column' : 'row')};
    justify-content: space-between;
  }

  .content {
    padding-bottom: 1rem;
    max-width: 36rem;
    ${(props) => props.theme.breakpoints.up('md')} {
      padding-right: 4rem;
    }
    ${(props) => props.theme.breakpoints.up('lg')} {
      max-width: 40vw;
    }
  }

  .markdown {
    min-width: 30vw;

    ${(props) => props.theme.breakpoints.up('xl')} {
      min-width: 40vw;
    }
    ol {
      list-style: none;
      counter-reset: list-counter;
      margin: 0;
      padding-top: 0.5rem;

      li {
        counter-increment: list-counter;
        padding-bottom: 1rem;
        position: relative;
        line-height: ${bulletSize};
        font-weight: bold;
        font-family: ${primaryFontFamily};
      }
      li::before {
        content: counter(list-counter);
        color: ${warm[900]};
        font-weight: bold;
        line-height: ${bulletSize};
        background: white;
        border-radius: 50%;
        width: ${bulletSize};
        height: ${bulletSize};
        margin-right: 0.5rem;
        position: absolute;
        top: 0;
        left: calc(-1 * ${bulletSize} - 10px);
        text-align: center;
      }
    }
  }
`;

interface MarkdownProps {
  flexDisplay: FlexDirection;
  smAndDown: boolean;
}
